// ── Documentos Tab ───────────────────────────────────────────────────────────
import { ref as dbRef, set, remove, get } from 'firebase/database';
import { ref as sRef, uploadBytesResumable,
         getDownloadURL, deleteObject }   from 'firebase/storage';
import { db, storage }                    from '@/firebase';
import { getState, getTrip,
         setTripState }                   from '@/store/tripState';
import { openModal }                      from '@/ui/modal';
import { toastSuccess, toastError }       from '@/ui/toast';
import { toArr, uid, docIcon,
         requireFields, sanitize }     from '@/helpers';
import { DOC_CATS }                       from '@/constants';
import { attachmentViewerUrl }            from '@/ui/attachmentViewer';
import type { TripDoc, DocCat }           from '@/types';



let docsTabCurrent: 'viagem' | 'global' = 'viagem';

export function switchDocsTab(tab: 'viagem' | 'global'): void {
  docsTabCurrent = tab;
  const v = document.getElementById('docs-viagem-section') as HTMLElement | null;
  const g = document.getElementById('docs-global-section') as HTMLElement | null;
  if (v) v.style.display = tab === 'viagem' ? 'block' : 'none';
  if (g) g.style.display = tab === 'global' ? 'block' : 'none';
  document.getElementById('docs-tab-viagem-btn')?.classList.toggle('active', tab === 'viagem');
  document.getElementById('docs-tab-global-btn')?.classList.toggle('active', tab === 'global');
  renderDocs();
}

export async function renderDocs(): Promise<void> {
  try {
    renderTripDocs();
    await renderGlobalDocs();
  } catch (e) {
    toastError('Erro ao carregar documentos: ' + (e instanceof Error ? e.message : String(e)));
  }
}

export function renderTripDocs(): void {
  const el  = document.getElementById('docs-viagem-list');
  if (!el) return;
  const arr = toArr(getTrip().docs ?? {}) as TripDoc[];
  arr.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR'));
  if (!arr.length) {
    el.innerHTML = '<div class="empty">Nenhum documento desta viagem.</div>';
    return;
  }
  const cats = [...new Set(arr.map(d => d.cat ?? 'Geral'))].sort();
  el.innerHTML = cats.map(cat => `
    <div class="doc-cat-header">${sanitize(cat)}</div>
    ${arr.filter(d => (d.cat ?? 'Geral') === cat).map(d => `
      <div class="doc-item" data-url="${sanitize(d.url)}" data-path="${sanitize(d.path)}" data-name="${sanitize(d.name)}" onclick="window.__openDocUrl(this)">
        <div class="doc-icon">${docIcon(d.name)}</div>
        <div class="doc-info">
          <div class="doc-name">${sanitize(d.name)}</div>
          <div class="doc-meta">${sanitize(d.cat ?? '')}${d.note ? ' · ' + sanitize(d.note) : ''}</div>
        </div>
        <button class="icon-btn del" onclick="event.stopPropagation();window.__delItem('docs','${d.id}')">×</button>
      </div>`).join('')}
  `).join('');
}

export async function renderGlobalDocs(): Promise<void> {
  const el = document.getElementById('docs-global-list');
  if (!el) return;
  const { currentUser } = getState();
  if (!currentUser) return;
  try {
    const snap = await get(dbRef(db, `userDocs/${currentUser.uid}`));
    const arr  = toArr((snap.val() ?? {}) as Record<string, TripDoc>) as TripDoc[];
    arr.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'pt-BR'));
    if (!arr.length) {
      el.innerHTML = '<div class="empty">Nenhum documento global.</div>';
      return;
    }
    const cats = [...new Set(arr.map(d => d.cat ?? 'Geral'))].sort();
    el.innerHTML = cats.map(cat => `
      <div class="doc-cat-header">${sanitize(cat)}</div>
      ${arr.filter(d => (d.cat ?? 'Geral') === cat).map(d => `
        <div class="doc-item" data-url="${sanitize(d.url)}" data-path="${sanitize(d.path)}" data-name="${sanitize(d.name)}" onclick="window.__openDocUrl(this)">
          <div class="doc-icon">${docIcon(d.name)}</div>
          <div class="doc-info">
            <div class="doc-name">${sanitize(d.name)}</div>
            <div class="doc-meta">${sanitize(d.cat ?? '')}${d.note ? ' · ' + sanitize(d.note) : ''}</div>
          </div>
          <button class="icon-btn del" onclick="event.stopPropagation();window.__delGlobalDoc('${d.id}')">×</button>
        </div>`).join('')}
    `).join('');
  } catch (e) {
    el.innerHTML = '<div class="empty">Erro ao carregar documentos.</div>';
    toastError('Erro: ' + (e instanceof Error ? e.message : String(e)));
  }
}

export function openDocModal(scope: 'viagem' | 'global'): void {
  const { currentTripId } = getState();
  openModal(
    scope === 'viagem' ? 'Adicionar documento da viagem' : 'Adicionar documento pessoal',
    `<div class="fg"><label>Descrição / Nome</label><input id="f-doc-name" placeholder="ex: Passaporte Renan"></div>
    <div class="fg"><label>Categoria</label>
      <select id="f-doc-cat">${DOC_CATS.map(c => `<option value="${c}">${c}</option>`).join('')}</select>
    </div>
    <div class="fg"><label>Nota (opcional)</label><input id="f-doc-note" placeholder="Validade, observações…"></div>
    <div class="fg">
      <label>Arquivo</label>
      <input type="file" id="f-doc-file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx">
    </div>
    <div id="doc-upload-progress" style="display:none;margin-top:6px">
      <div style="font-size:11px;color:var(--muted);margin-bottom:3px" id="doc-upload-label">Enviando...</div>
      <div style="height:4px;background:var(--bg3);border-radius:999px;overflow:hidden">
        <div id="doc-upload-fill" style="height:100%;width:0%;background:var(--lux-blue);border-radius:999px;transition:width .2s"></div>
      </div>
    </div>`,
    async () => {
      const { currentUser } = getState();
      if (!currentUser) return;
      requireFields([{ id: 'f-doc-name', label: 'Nome do documento' }]);
      const name = (document.getElementById('f-doc-name') as HTMLInputElement)?.value.trim();
      const cat  = (document.getElementById('f-doc-cat')  as HTMLSelectElement)?.value as DocCat;
      const note = (document.getElementById('f-doc-note') as HTMLInputElement)?.value.trim();
      const file = (document.getElementById('f-doc-file') as HTMLInputElement)?.files?.[0];
      if (!file) throw new Error('Selecione um arquivo.');

      const docId = uid();
      const path  = `docs/${scope === 'viagem' ? currentTripId : currentUser.uid}/${docId}_${file.name}`;
      const sref  = sRef(storage, path);
      const wrap  = document.getElementById('doc-upload-progress') as HTMLElement | null;
      const fill  = document.getElementById('doc-upload-fill')    as HTMLElement | null;
      const label = document.getElementById('doc-upload-label')   as HTMLElement | null;
      if (wrap) wrap.style.display = 'block';

      await new Promise<void>((resolve, reject) => {
        const task = uploadBytesResumable(sref, file);
        task.on('state_changed',
          snap => {
            const pct = Math.round(snap.bytesTransferred / snap.totalBytes * 100);
            if (fill)  fill.style.width    = pct + '%';
            if (label) label.textContent   = `Enviando... ${pct}%`;
          },
          reject,
          resolve
        );
      });

      const url = await getDownloadURL(sref);
      const obj: TripDoc = { id: docId, name, cat, note: note ?? '', url, path, createdAt: Date.now() };

      if (scope === 'viagem' && currentTripId) {
        await set(dbRef(db, `trips/${currentTripId}/docs/${docId}`), obj);
        const trip = getTrip();
        setTripState({ ...trip, docs: { ...trip.docs, [docId]: obj } });
        renderTripDocs();
      } else {
        await set(dbRef(db, `userDocs/${currentUser.uid}/${docId}`), obj);
        await renderGlobalDocs();
      }
      toastSuccess('Documento adicionado!');
    }
  );
}

export async function delGlobalDoc(docId: string): Promise<void> {
  const { currentUser } = getState();
  if (!currentUser) return;
  try {
    const snap = await get(dbRef(db, `userDocs/${currentUser.uid}/${docId}`));
    const doc  = snap.val() as TripDoc | null;
    if (doc?.path) deleteObject(sRef(storage, doc.path)).catch(() => void 0);
    await remove(dbRef(db, `userDocs/${currentUser.uid}/${docId}`));
    toastSuccess('Documento removido.');
    await renderGlobalDocs();
  } catch (e) {
    toastError('Erro ao remover: ' + (e instanceof Error ? e.message : String(e)));
  }
}

/** Safely open a document URL from a data-url attribute (prevents JS-injection in onclick). */
export function openDocUrl(el: HTMLElement): void {
  const url = el.dataset.url ?? '';
  const path = el.dataset.path ?? '';
  const name = el.dataset.name ?? 'Documento';
  const target = attachmentViewerUrl({ path, name, url });
  if (target !== '#' && (target.startsWith('/') || /^https?:\/\//i.test(target))) {
    window.open(target, '_blank', 'noopener,noreferrer');
  }
}

// Suppress unused variable (docsTabCurrent used for future tab state tracking)
void docsTabCurrent;
